import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    model,
    signal,
    type ElementRef,
    Injector,
    viewChild,
} from '@angular/core';
import { form, FormField, disabled, maxLength } from '@angular/forms/signals';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgIcon } from '@ng-icons/core';
import {
    type InputSize,
    QpxButton,
    QpxFormFieldDirectives,
    QpxIcon,
} from '../../directives';
import { QpxFilterBar } from '../filter/bar/filter-bar';
import { FILTER_LAYOUT_CONTEXT } from '../filter/filter-layout';

export type QpxSearchAppearance = 'elevated' | 'outlined';

@Component({
    selector: 'qpx-search',
    imports: [
        FormField,
        MatBadgeModule,
        MatButtonModule,
        MatFormFieldModule,
        NgIcon,
        MatInputModule,
        QpxButton,
        QpxFormFieldDirectives,
        QpxIcon,
    ],
    templateUrl: './search.html',
    styleUrl: './search.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.data-appearance]': 'appearance()',
        '[attr.data-active]': 'hasCollapsedValue()',
        '[attr.data-expanded]': 'isExpanded()',
        '[class.collapsible]': 'collapsible()',
        '[class.expanded]': 'isExpanded()',
        '[class.small]': 'size() === "sm"',
        '(focusout)': 'collapseSearch($event)',
    },
})
export class QpxSearch {
    //#region Dependency injection
    private readonly injector = inject(Injector);
    private readonly filterBar = inject(QpxFilterBar, { optional: true });

    /**
     * Optional filter layout context.
     * Used to detect container layout for responsive behavior.
     */
    private readonly filterLayout = inject(FILTER_LAYOUT_CONTEXT, {
        optional: true,
    });
    //#endregion

    //#region Inputs
    public readonly value = model.required<string>();
    public readonly inputId = input.required<string>();
    public readonly placeholderText = input.required<string>();
    public readonly label = input<string>();
    public readonly fieldClasses = input('w-full');
    public readonly disabled = input(false);
    public readonly size = input<InputSize>('md');
    public readonly appearance = input<QpxSearchAppearance>('elevated');
    public readonly collapsible = input(this.filterBar !== null);
    public readonly collapseOnBlurWithValue = input(false);
    //#endregion

    //#region Queries
    private readonly searchInput =
        viewChild.required<ElementRef<HTMLInputElement>>('searchInput');
    //#endregion

    //#region State
    protected readonly displayLabel = computed(
        () => this.label() || this.placeholderText()
    );

    protected readonly valueField = form(this.value, (path) => {
        maxLength(path, 50);
        disabled(path, () => this.disabled());
    });

    private readonly expansionRequested = signal(false);
    private readonly collapseWithValue = computed(
        () => this.filterBar?.overlaySearch() || this.collapseOnBlurWithValue()
    );

    protected readonly isExpanded = computed(
        () =>
            !this.collapsible() ||
            this.expansionRequested() ||
            (!this.collapseWithValue() && Boolean(this.value().trim().length))
    );

    protected readonly hasCollapsedValue = computed(
        () =>
            this.collapsible() &&
            this.collapseWithValue() &&
            !this.isExpanded() &&
            Boolean(this.value().trim().length)
    );

    protected readonly triggerLabel = computed(() =>
        this.hasCollapsedValue()
            ? `${this.displayLabel()}, search active`
            : this.displayLabel()
    );

    /**
     * Whether the search input is currently in list/block layout.
     * Returns false when used standalone (outside FilterGroup).
     */
    protected readonly isListLayout = computed(
        () => this.filterLayout?.layoutMode() === 'block'
    );

    /**
     * Combined field classes including conditional within-list class.
     */
    protected readonly allFieldClasses = computed(() => {
        const baseClasses = this.fieldClasses();
        const withinList = this.isListLayout() ? 'within-list' : '';
        return ['search-field', baseClasses, withinList]
            .filter(Boolean)
            .join(' ');
    });
    //#endregion

    //#region Template methods
    protected clearSearch(): void {
        if (this.disabled()) return;

        if (this.collapsible()) this.expandSearch();
        this.value.set('');
    }

    protected expandSearch(): void {
        if (this.disabled()) return;

        this.expansionRequested.set(true);
        afterNextRender(() => this.searchInput().nativeElement.focus(), {
            injector: this.injector,
        });
    }

    protected collapseSearch(event: FocusEvent): void {
        const nextTarget = event.relatedTarget;
        const host = event.currentTarget;

        if (
            host instanceof HTMLElement &&
            nextTarget instanceof Node &&
            host.contains(nextTarget)
        ) {
            return;
        }

        this.expansionRequested.set(false);
    }
    //#endregion
}
